import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ViewerSettings } from "../../types/settings";
import { useItemModalDraft, EditableItem } from "../../hooks/useItemModalDraft";
import { ItemImageUploader } from "./ItemImageUploader";
import { ItemMetadataForm } from "./ItemMetadataForm";
import { DiscardChangesDialog } from "./DiscardChangesDialog";

type LanguageCode = ViewerSettings["language"];

const ITEM_MODAL_COPY: Record<
  LanguageCode,
  {
    closeModal: string;
    tool: string;
    part: string;
    itemId: string;
    name: string;
    id: string;
    code: string;
    category: string;
    status: string;
    available: string;
    unavailable: string;
    description: string;
    orderThis: string;
    order: string;
    previewSuffix: string;
    uploadImage: string;
    saveChanges: string;
    cancel: string;
    discardChangesTitle: string;
    discardChangesBody: string;
    discard: string;
    keepEditing: string;
    changesSaved: string;
  }
> = {
  en: {
    closeModal: "Close modal",
    tool: "Tool",
    part: "Part",
    itemId: "ID",
    name: "Name",
    id: "ID",
    code: "Code",
    category: "Category",
    status: "Status",
    available: "Available",
    unavailable: "Unavailable",
    description: "Description",
    orderThis: "Order this",
    order: "Order",
    previewSuffix: "preview",
    uploadImage: "Upload Image",
    saveChanges: "Save Changes",
    cancel: "Cancel",
    discardChangesTitle: "Discard unsaved changes?",
    discardChangesBody:
      "You have unsaved edits in this modal. Discard them and close?",
    discard: "Discard",
    keepEditing: "Keep editing",
    changesSaved: "Changes saved.",
  },
  es: {
    closeModal: "Cerrar modal",
    tool: "Herramienta",
    part: "Pieza",
    itemId: "ID",
    name: "Nombre",
    id: "ID",
    code: "Codigo",
    category: "Categoria",
    status: "Estado",
    available: "Disponible",
    unavailable: "No disponible",
    description: "Descripcion",
    orderThis: "Pedir esta",
    order: "Pedir",
    previewSuffix: "vista previa",
    uploadImage: "Subir imagen",
    saveChanges: "Guardar cambios",
    cancel: "Cancelar",
    discardChangesTitle: "Descartar cambios sin guardar?",
    discardChangesBody:
      "Tienes cambios sin guardar en esta ventana. Deseas descartarlos y cerrar?",
    discard: "Descartar",
    keepEditing: "Seguir editando",
    changesSaved: "Cambios guardados.",
  },
  fr: {
    closeModal: "Fermer la fenetre",
    tool: "Outil",
    part: "Piece",
    itemId: "ID",
    name: "Nom",
    id: "ID",
    code: "Code",
    category: "Categorie",
    status: "Statut",
    available: "Disponible",
    unavailable: "Indisponible",
    description: "Description",
    orderThis: "Commander cet",
    order: "Commander",
    previewSuffix: "apercu",
    uploadImage: "Televerser une image",
    saveChanges: "Enregistrer",
    cancel: "Annuler",
    discardChangesTitle: "Ignorer les modifications non enregistrees?",
    discardChangesBody:
      "Vous avez des modifications non enregistrees. Les ignorer et fermer?",
    discard: "Ignorer",
    keepEditing: "Continuer",
    changesSaved: "Modifications enregistrees.",
  },
  de: {
    closeModal: "Modal schliessen",
    tool: "Werkzeug",
    part: "Teil",
    itemId: "ID",
    name: "Name",
    id: "ID",
    code: "Code",
    category: "Kategorie",
    status: "Status",
    available: "Verfugbar",
    unavailable: "Nicht verfugbar",
    description: "Beschreibung",
    orderThis: "Dieses bestellen",
    order: "Bestellen",
    previewSuffix: "Vorschau",
    uploadImage: "Bild hochladen",
    saveChanges: "Anderungen speichern",
    cancel: "Abbrechen",
    discardChangesTitle: "Ungespeicherte Anderungen verwerfen?",
    discardChangesBody:
      "Es gibt ungespeicherte Anderungen. Verwerfen und schliessen?",
    discard: "Verwerfen",
    keepEditing: "Weiter bearbeiten",
    changesSaved: "Anderungen gespeichert.",
  },
  zh: {
    closeModal: "关闭窗口",
    tool: "工具",
    part: "部件",
    itemId: "编号",
    name: "名称",
    id: "编号",
    code: "代码",
    category: "类别",
    status: "状态",
    available: "可用",
    unavailable: "不可用",
    description: "说明",
    orderThis: "订购该",
    order: "订购",
    previewSuffix: "预览",
    uploadImage: "上传图片",
    saveChanges: "保存更改",
    cancel: "取消",
    discardChangesTitle: "放弃未保存的更改？",
    discardChangesBody: "此窗口中有未保存的更改。确定放弃并关闭吗？",
    discard: "放弃",
    keepEditing: "继续编辑",
    changesSaved: "更改已保存。",
  },
};

interface ItemViewModalProps<T extends EditableItem> {
  isOpen: boolean;
  onClose: () => void;
  item: T | null;
  itemType: "tool" | "part";
  onOrder?: (item: T, itemType: "tool" | "part") => void;
  onSaved?: (item: T, itemType: "tool" | "part") => void;
  isAdmin?: boolean;
  language?: LanguageCode;
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  iconColor?: string;
  accentColor?: string;
  fontFamily?: string;
  uploadImage?: (
    itemType: "tool" | "part",
    entityId: string,
    file: File,
  ) => Promise<string>;
  updateItem?: (
    itemType: "tool" | "part",
    id: string,
    payload: {
      name: string;
      code?: string;
      category?: string;
      available: boolean;
      imageUrl?: string;
    },
  ) => Promise<Partial<T>>;
}

export function ItemViewModal<T extends EditableItem>({
  isOpen,
  onClose,
  item,
  itemType,
  onOrder,
  onSaved,
  isAdmin = false,
  language = "en",
  backgroundColor = "rgba(255, 255, 255, 0.04)",
  backdropBlur = "40px",
  borderColor = "rgba(255, 255, 255, 0.1)",
  boxShadow = "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
  primaryTextColor = "rgba(255, 255, 255, 0.95)",
  secondaryTextColor = "rgba(255, 255, 255, 0.7)",
  iconColor = "rgba(255, 255, 255, 0.8)",
  accentColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
  uploadImage,
  updateItem,
}: ItemViewModalProps<T>) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const copy = ITEM_MODAL_COPY[language] ?? ITEM_MODAL_COPY.en;
  const itemTypeIcon =
    itemType === "tool" ? "build" : "precision_manufacturing";
  const itemLabel = itemType === "tool" ? copy.tool : copy.part;
  const {
    draft,
    setField,
    imagePreviewUrl,
    uploadFile,
    setUploadFile,
    errors,
    isDirty,
    isSaving,
    save,
    reset,
    saveMessage,
  } = useItemModalDraft({ item, itemType, onSaved, uploadImage, updateItem });

  const handleOrder = () => {
    if (!item) return;
    onOrder?.(item, itemType);
  };

  const requestClose = () => {
    if (isSaving) return;
    if (isAdmin && isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      window.setTimeout(() => setIsVisible(true), 10);
      setImgError(false);
      setShowDiscardDialog(false);
    } else {
      setIsVisible(false);
      window.setTimeout(() => setShouldRender(false), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        if (showDiscardDialog) {
          setShowDiscardDialog(false);
          return;
        }
        requestClose();
        return;
      }

      if (event.key === "Enter") {
        if (isAdmin) {
          event.preventDefault();
          void save();
        } else if (item && onOrder) {
          event.preventDefault();
          handleOrder();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    isAdmin,
    isOpen,
    item,
    onOrder,
    save,
    showDiscardDialog,
    isDirty,
    isSaving,
  ]);

  if (!shouldRender) return null;

  return createPortal(
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 210,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(15, 17, 21, 0.6)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
          fontFamily,
          padding: "1.5rem",
        }}
        onClick={requestClose}
        role="presentation"
        data-oid="untpx9o"
      >
        <div
          style={{
            maxWidth: "520px",
            width: "100%",
            maxHeight: "90vh",
            borderRadius: "2rem",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            backgroundColor,
            backdropFilter: `blur(${backdropBlur})`,
            WebkitBackdropFilter: `blur(${backdropBlur})`,
            border: `1px solid ${borderColor}`,
            boxShadow,
            transform: isVisible ? "scale(1)" : "scale(0.95)",
            transition: "transform 0.3s ease-in-out",
          }}
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="item-modal-title"
          aria-describedby="item-modal-description"
          data-oid="u2vj6mk"
        >
          <button
            onClick={requestClose}
            aria-label={copy.closeModal}
            style={{
              position: "absolute",
              top: "1.5rem",
              right: "1.5rem",
              width: "2.5rem",
              height: "2.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "9999px",
              background: "transparent",
              border: "none",
              color: secondaryTextColor,
              cursor: "pointer",
              transition: "all 0.3s",
              zIndex: 10,
            }}
            data-oid="4o80lcm"
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "1.25rem", fontWeight: 300 }}
              data-oid="5n4:8bc"
            >
              close
            </span>
          </button>

          <div style={{ padding: "2rem 2rem 1rem 2rem" }} data-oid="txl9_82">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "0.25rem",
              }}
              data-oid="464zzcj"
            >
              <span
                className="material-symbols-outlined"
                style={{
                  color: accentColor,
                  fontSize: "1.5rem",
                  fontWeight: 300,
                }}
                data-oid="nfgt9t_"
              >
                {itemTypeIcon}
              </span>
              <h2
                id="item-modal-title"
                style={{
                  margin: 0,
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  letterSpacing: "0.025em",
                  color: primaryTextColor,
                  fontFamily,
                }}
                data-oid="85vjdij"
              >
                {draft.name || item?.name}
              </h2>
            </div>
            <p
              id="item-modal-description"
              style={{
                margin: 0,
                fontSize: "0.875rem",
                fontWeight: 400,
                color: secondaryTextColor,
                fontFamily,
              }}
              data-oid="q9d7ob2"
            >
              {itemLabel} {copy.itemId}: {draft.id}
            </p>
          </div>

          <div
            style={{
              flex: 1,
              padding: "1rem 2rem",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
            }}
            data-oid="8g.v2-9"
          >
            <div style={{ width: "100%" }} data-oid="nryf62-">
              {imagePreviewUrl && !imgError ? (
                <img
                  src={imagePreviewUrl}
                  alt={`${draft.name || item?.name || itemLabel} ${copy.previewSuffix}`}
                  onError={() => setImgError(true)}
                  style={{
                    width: "100%",
                    maxHeight: "300px",
                    objectFit: "cover",
                    borderRadius: "1rem",
                    display: "block",
                  }}
                  data-oid="rykokcc"
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    aspectRatio: "16/9",
                    borderRadius: "1rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: `1px solid ${borderColor}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  data-oid="15fqeqv"
                >
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: "3rem",
                      color: iconColor,
                      fontWeight: 300,
                    }}
                    data-oid="e3blyat"
                  >
                    {itemTypeIcon}
                  </span>
                </div>
              )}
            </div>

            <ItemMetadataForm
              isAdmin={isAdmin}
              draft={draft}
              errors={errors}
              onFieldChange={setField}
              readOnlyFields={["id"]}
              labels={{
                name: copy.name,
                id: copy.id,
                code: copy.code,
                category: copy.category,
                status: copy.status,
                available: copy.available,
                unavailable: copy.unavailable,
                description: copy.description,
              }}
              description={
                (item as { description?: string } | null)?.description
              }
              primaryTextColor={primaryTextColor}
              secondaryTextColor={secondaryTextColor}
              borderColor={borderColor}
              fontFamily={fontFamily}
              data-oid=".49l:8x"
            />

            {isAdmin ? (
              <ItemImageUploader
                file={uploadFile}
                previewUrl={imagePreviewUrl}
                error={errors.image}
                onFileSelected={setUploadFile}
                buttonLabel={copy.uploadImage}
                showButton={false}
                fontFamily={fontFamily}
                borderColor={borderColor}
                primaryTextColor={primaryTextColor}
                secondaryTextColor={secondaryTextColor}
                accentColor={accentColor}
                data-oid="fhu5gg9"
              />
            ) : null}
          </div>

          <div
            style={{
              padding: "2rem",
              marginTop: "0.5rem",
              borderTop: `1px solid ${borderColor}`,
              display: "flex",
              flexDirection: "column",
              gap: "0.9rem",
            }}
            data-oid="ojfes1:"
          >
            {errors.submit ? (
              <p
                role="alert"
                style={{
                  margin: 0,
                  fontSize: "0.8rem",
                  color: "#f87171",
                  fontFamily,
                }}
                data-oid="yicoz_k"
              >
                {errors.submit}
              </p>
            ) : null}
            {saveMessage ? (
              <p
                aria-live="polite"
                style={{
                  margin: 0,
                  fontSize: "0.8rem",
                  color: accentColor,
                  fontFamily,
                }}
                data-oid="-27fmj-"
              >
                {copy.changesSaved}
              </p>
            ) : null}
            {isAdmin ? (
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                }}
                data-oid="q_456ic"
              >
                <button
                  type="button"
                  onClick={reset}
                  disabled={isSaving}
                  style={{
                    padding: "0.75rem 1rem",
                    borderRadius: "9999px",
                    border: `1px solid ${borderColor}`,
                    background: "transparent",
                    color: primaryTextColor,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    fontFamily,
                    cursor: isSaving ? "not-allowed" : "pointer",
                    textTransform: "uppercase",
                    opacity: isSaving ? 0.6 : 1,
                  }}
                  data-oid="hlhjq9n"
                >
                  {copy.cancel}
                </button>
                <button
                  type="button"
                  onClick={() => void save()}
                  disabled={isSaving}
                  style={{
                    padding: "0.75rem 1rem",
                    borderRadius: "9999px",
                    border: `1px solid ${borderColor}`,
                    background: "rgba(255, 255, 255, 0.08)",
                    color: primaryTextColor,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    fontFamily,
                    cursor: isSaving ? "not-allowed" : "pointer",
                    textTransform: "uppercase",
                    opacity: isSaving ? 0.6 : 1,
                  }}
                  data-oid="ws8c84p"
                >
                  {isSaving ? `${copy.saveChanges}...` : copy.saveChanges}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    (
                      document.querySelector(
                        'input[aria-label="Upload image"]',
                      ) as HTMLInputElement | null
                    )?.click()
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    padding: "0.75rem 1rem",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    borderRadius: "9999px",
                    background: accentColor,
                    color: "white",
                    border: "none",
                    cursor: "pointer",
                    textTransform: "uppercase",
                    boxShadow: `0 0 20px ${accentColor}4D`,
                    fontFamily,
                  }}
                  data-oid="he3q92."
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "1rem", fontWeight: 300 }}
                    data-oid="4s7180-"
                  >
                    upload
                  </span>
                  {copy.uploadImage}
                </button>
              </div>
            ) : (
              <button
                onClick={handleOrder}
                disabled={!item}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  padding: "0.75rem 2rem",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  borderRadius: "9999px",
                  background: accentColor,
                  color: "white",
                  border: "none",
                  cursor: item ? "pointer" : "not-allowed",
                  textTransform: "uppercase",
                  boxShadow: `0 0 20px ${accentColor}4D`,
                  fontFamily,
                  opacity: item ? 1 : 0.6,
                }}
                aria-label={`${copy.orderThis} ${itemLabel.toLowerCase()}`}
                data-oid="7m3jozp"
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "1rem", fontWeight: 300 }}
                  data-oid="1kv3w9n"
                >
                  shopping_cart
                </span>
                {copy.order}
              </button>
            )}
          </div>
        </div>
      </div>

      <DiscardChangesDialog
        isOpen={showDiscardDialog}
        onDiscard={() => {
          setShowDiscardDialog(false);
          reset();
          onClose();
        }}
        onContinueEditing={() => setShowDiscardDialog(false)}
        title={copy.discardChangesTitle}
        message={copy.discardChangesBody}
        discardLabel={copy.discard}
        continueLabel={copy.keepEditing}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        boxShadow={boxShadow}
        primaryTextColor={primaryTextColor}
        secondaryTextColor={secondaryTextColor}
        accentColor={accentColor}
        fontFamily={fontFamily}
        data-oid="ojniqux"
      />
    </>,
    document.body,
  );
}
